import { create } from 'zustand';
import type { AxiosError } from 'axios';
import type { User } from '../types/user';
import * as authService from '../services/authService';
import wsService from '../services/websocketService';
import api from '../services/api';
import { clearRefreshMark } from '../services/sessionRefresh';

interface PagePermissions {
  [path: string]: { [role: string]: boolean };
}

interface FeaturePermissions {
  [key: string]: { [role: string]: boolean };
}

interface AuthState {
  token: string | null;
  user: User | null;
  loading: boolean;
  permissionsLoaded: boolean;
  pagePermissions: PagePermissions;
  featurePermissions: FeaturePermissions;
  idleTimeoutMinutes: number;
  alarmTypeLabels: Record<string, string>;
  // 警報類型通知角色過濾：{ alarm_type: { role: bool } }，空/未設定 = 所有角色皆接收
  alarmRoleFilter: Record<string, Record<string, boolean>>;
  login: (username: string, password: string) => Promise<void>;
  logout: (reason?: 'manual' | 'idle') => void;
  restoreSession: () => Promise<void>;
  fetchPermissions: () => Promise<void>;
  setPagePermissions: (p: PagePermissions) => void;
  setFeaturePermissions: (p: FeaturePermissions) => void;
  setIdleTimeoutMinutes: (m: number) => void;
  setAlarmTypeLabels: (labels: Record<string, string>) => void;
  setAlarmRoleFilter: (f: Record<string, Record<string, boolean>>) => void;
}

// 認證 token 已改存 httpOnly cookie（JS 讀不到）。store 內 `token` 僅作為
// 「本裝置已登入」的哨兵旗標，供 UI gate / WS 重連判斷；真正憑證在 cookie。
// 重新整理後 cookie 仍在，靠 localStorage 的 'authed' 旗標還原哨兵，再以 cookie 向後端驗證。
const SESSION_SENTINEL = 'cookie';

const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('authed') === '1' ? SESSION_SENTINEL : null,
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  loading: false,
  permissionsLoaded: false,
  pagePermissions: {},
  featurePermissions: {},
  idleTimeoutMinutes: 30,
  alarmTypeLabels: {},
  alarmRoleFilter: {},

  login: async (username, password) => {
    set({ loading: true });
    try {
      // 登入成功後，後端已把 JWT 寫入 httpOnly cookie；前端不再保存明碼 token
      await authService.login(username, password);
      localStorage.setItem('authed', '1');
      const token = SESSION_SENTINEL;

      // 先設哨兵 token（gate 需要），但不設 user — sidebar 靠 user 判斷是否渲染
      set({ token });

      const user = await authService.getMe() as User;
      localStorage.setItem('user', JSON.stringify(user));

      wsService.connect(token);

      // 取得權限後再一次性設定 user + permissions，避免 sidebar 閃現錯誤選單
      const [pageRes, featureRes, sessionRes, labelsRes, roleFilterRes] = await Promise.all([
        api.get('/system/page-permissions'),
        api.get('/system/feature-permissions'),
        api.get('/system/session-config'),
        api.get('/system/alarm-type-labels'),
        api.get('/system/alarm-role-filter'),
      ]);
      set({
        user,
        pagePermissions: pageRes.data.data || {},
        featurePermissions: featureRes.data.data || {},
        idleTimeoutMinutes: sessionRes.data.data?.idle_timeout_minutes ?? 30,
        alarmTypeLabels: labelsRes.data.data || {},
        alarmRoleFilter: roleFilterRes.data.data || {},
        permissionsLoaded: true,
        loading: false,
      });
    } catch (e) {
      set({ loading: false });
      throw e;
    }
  },

  logout: (reason: 'manual' | 'idle' = 'manual') => {
    const clearLocal = () => {
      localStorage.removeItem('authed');
      localStorage.removeItem('user');
      clearRefreshMark();   // 清續期時間戳：下次登入的第一次續期不該被冷卻窗吃掉
      wsService.disconnect();
      set({ token: null, user: null, permissionsLoaded: false, pagePermissions: {}, featurePermissions: {}, alarmTypeLabels: {}, alarmRoleFilter: {} });
    };
    // 先通知後端寫入登出稽核並清除 httpOnly cookie（此時 cookie 仍在，請求才通得過），
    // 待請求完成（成功/失敗/逾時）後才清除本地旗標，避免 session 先被清導致 401 而漏記。
    if (get().token) {
      authService.logout(reason).catch(() => {}).finally(clearLocal);
    } else {
      clearLocal();
    }
  },

  restoreSession: async () => {
    const token = get().token;
    if (!token) return;
    // 還原期間先清除 user，避免 sidebar 用舊 user + 空權限渲染
    set({ user: null, permissionsLoaded: false });
    try {
      const user = await authService.getMe() as User;
      localStorage.setItem('user', JSON.stringify(user));
      wsService.connect(token);

      // 取得權限後再一次性設定 user + permissions
      const [pageRes, featureRes, sessionRes, labelsRes, roleFilterRes] = await Promise.all([
        api.get('/system/page-permissions'),
        api.get('/system/feature-permissions'),
        api.get('/system/session-config'),
        api.get('/system/alarm-type-labels'),
        api.get('/system/alarm-role-filter'),
      ]);
      set({
        user,
        pagePermissions: pageRes.data.data || {},
        featurePermissions: featureRes.data.data || {},
        idleTimeoutMinutes: sessionRes.data.data?.idle_timeout_minutes ?? 30,
        alarmTypeLabels: labelsRes.data.data || {},
        alarmRoleFilter: roleFilterRes.data.data || {},
        permissionsLoaded: true,
      });
    } catch (e) {
      // 只有「憑證確實失效」（401/403）才登出；暫時性錯誤（網路中斷、後端重啟中、5xx、逾時）
      // 不可把使用者踢出——否則開頁/重整時剛好撞上後端 nssm restart 就會誤登出（登入穩定性痛點）。
      const st = (e as AxiosError)?.response?.status;
      if (st === 401 || st === 403) {
        get().logout();
        return;
      }
      // 暫時性失敗：保留 cookie 哨兵，用 localStorage 快取的 user 還原 UI，權限走 graceful 重載
      // （fetchPermissions 失敗會落預設並標記 permissionsLoaded，不會卡 spinner）。
      // cookie 若其實已失效，下一個受保護 API 呼叫會收 401，由 api.ts 攔截器導回登入。
      const cached = JSON.parse(localStorage.getItem('user') || 'null') as User | null;
      if (cached) {
        set({ user: cached });
        wsService.connect(token);
      }
      await get().fetchPermissions();
    }
  },

  fetchPermissions: async () => {
    try {
      const [pageRes, featureRes, sessionRes, labelsRes, roleFilterRes] = await Promise.all([
        api.get('/system/page-permissions'),
        api.get('/system/feature-permissions'),
        api.get('/system/session-config'),
        api.get('/system/alarm-type-labels'),
        api.get('/system/alarm-role-filter'),
      ]);
      set({
        pagePermissions: pageRes.data.data || {},
        featurePermissions: featureRes.data.data || {},
        idleTimeoutMinutes: sessionRes.data.data?.idle_timeout_minutes ?? 30,
        alarmTypeLabels: labelsRes.data.data || {},
        alarmRoleFilter: roleFilterRes.data.data || {},
        permissionsLoaded: true,
      });
    } catch {
      // 權限 API 失敗時使用預設值（DEFAULT_PAGE_MIN_LEVEL），仍標記已載入避免永久 spinner
      set({ permissionsLoaded: true });
    }
  },

  setPagePermissions: (p) => set({ pagePermissions: p }),
  setFeaturePermissions: (p) => set({ featurePermissions: p }),
  setIdleTimeoutMinutes: (m) => set({ idleTimeoutMinutes: m }),
  setAlarmTypeLabels: (labels) => set({ alarmTypeLabels: labels }),
  setAlarmRoleFilter: (f) => set({ alarmRoleFilter: f }),
}));

export default useAuthStore;
